import { useMediaQuery } from './useMediaQuery';
import { BREAKPOINTS } from '../constants/breakpoints';

/**
 * Type representing the possible breakpoint names.
 * Maps to the keys in BREAKPOINTS constant.
 */
export type BreakpointKey = keyof typeof BREAKPOINTS;

/**
 * Hook to get the current responsive breakpoint name.
 * Uses existing BREAKPOINTS constants and useMediaQuery hook.
 *
 * Breakpoint thresholds (min-width):
 * - 'mobile': < 768px (default, below tablet threshold)
 * - 'tablet': >= 768px and < 1024px
 * - 'desktop': >= 1024px and < 1200px
 * - 'wide': >= 1200px
 *
 * Note: The breakpoint names are based on the MINIMUM width at which that
 * breakpoint activates. This follows mobile-first responsive design.
 *
 * @returns Current breakpoint: 'mobile' | 'tablet' | 'desktop' | 'wide'
 *
 * @example
 * const breakpoint = useBreakpoint();
 * if (breakpoint === 'mobile') {
 *   // Render mobile layout
 * }
 */
export function useBreakpoint(): BreakpointKey {
  // Check breakpoints from largest to smallest using min-width queries
  const isWide = useMediaQuery(`(min-width: ${BREAKPOINTS.wide}px)`);
  const isDesktop = useMediaQuery(`(min-width: ${BREAKPOINTS.desktop}px)`);
  const isTablet = useMediaQuery(`(min-width: ${BREAKPOINTS.tablet}px)`);

  // Return the largest matching breakpoint
  if (isWide) return 'wide';
  if (isDesktop) return 'desktop';
  if (isTablet) return 'tablet';
  return 'mobile';
}

/**
 * Convenience hook to check if viewport is mobile-sized.
 * Returns true when width < 768px (below tablet threshold).
 *
 * Use this for simple boolean checks instead of useBreakpoint()
 * when you only need to distinguish mobile from larger screens.
 *
 * @returns true when viewport width < BREAKPOINTS.tablet (768px)
 *
 * @example
 * const isMobile = useIsMobile();
 * return isMobile ? <MobileNav /> : <DesktopNav />;
 */
export function useIsMobile(): boolean {
  // Mobile = below tablet threshold (max-width query)
  return useMediaQuery(`(max-width: ${BREAKPOINTS.tablet - 1}px)`);
}

/**
 * Convenience hook to check if viewport is desktop-sized or larger.
 * Returns true when width >= 1024px.
 *
 * Use this for simple boolean checks instead of useBreakpoint()
 * when you only need to distinguish desktop from smaller screens.
 *
 * @returns true when viewport width >= BREAKPOINTS.desktop (1024px)
 *
 * @example
 * const isDesktop = useIsDesktop();
 * return isDesktop ? <Sidebar /> : null;
 */
export function useIsDesktop(): boolean {
  // Desktop = at or above desktop threshold (min-width query)
  return useMediaQuery(`(min-width: ${BREAKPOINTS.desktop}px)`);
}
